import { adminGuard, emailVerifiedGuard } from 'auth';
import { AppointmentsPageComponent, UserTreePageComponent } from 'credo';
import { UsersPageComponent } from './admin/containers/users-page/users-page.component';
import { XshopAdminPageComponent } from './admin/containers/xshop-admin-page/xshop-admin-page.component';
import { BookingsPageComponent } from './containers/pages/bookings-page/bookings-page.component';
import { DeleteAccountPageComponent } from './containers/pages/delete-account-page/delete-account-page.component';
import { MainPageComponent } from './containers/pages/main-page/main-page.component';
import { ProgramCatalogPageComponent } from './containers/pages/program-catalog/program-catalog-page.component';
import { CourseDetailPageComponent } from './containers/pages/program-catalog/course-detail-page.component';
import { CourseViewerPageComponent } from './containers/pages/program-catalog/course-viewer-page.component';

import { routeBuilder } from 'credo';

/**
 * Wolfpack Assets (wolfpack-assets.com) — config domain 7, user tenant 0 (fac3less).
 *
 * Derived from the distribution app, with three route groups deliberately dropped
 * because config domain 7 has no backend for them:
 *
 *   - Funnel (/r, /quiz/*): gated by WOLFPACK_FUNNEL_DOMAIN_IDS = [4] in
 *     funnel/{leads,quiz}_controller.rb. Domain 7 is not in that list.
 *   - User-facing KYC (/kyc/*): ViaCarte only, see menu.service.ts.
 *   - My Cards (/mycards): same reason.
 *
 * There is no root ('') route: the bare domain is served by nginx from the static
 * marketing landing (assets/landing/index.html), not by Angular. The route
 * builder's default '' -> /en redirect covers client-side navigation to /.
 */
export const routes = routeBuilder()
  .main(MainPageComponent)
  .withMaintenance()
  .withLegalAcknowledgement()
  .withCommunity()
  // Config domain 7 publishes its policy documents as terms / privacy /
  // imprint (see data/media/7/en/policy/). The login page's built-in fallback
  // asks for the fac3less names (terms_of_service / privacy_policy /
  // legal_notice), which do not exist here — every link came back 500
  // ActionView::MissingTemplate.
  .withLoginActions([
    { labelKey: 'policy.privacy',     policy: 'privacy' },
    { labelKey: 'policy.tos',         policy: 'terms' },
    { labelKey: 'policy.legalNotice', policy: 'imprint' }
  ])
  .protected(
    {
      path: 'program-catalog',
      component: ProgramCatalogPageComponent,
      data: { title: 'Program Catalog', animation: 'program-catalog' }
    },
    {
      // Course detail + buy (not-yet-owned courses).
      path: 'program-catalog/:programId',
      component: CourseDetailPageComponent,
      data: { title: 'Program Catalog', animation: 'program-catalog' }
    },
    {
      // Course content viewer (free/owned) — chapters + PDF viewer.
      path: 'course/:courseRef',
      component: CourseViewerPageComponent,
      data: { title: 'Course', animation: 'course' }
    },
    {
      path: 'users',
      component: UsersPageComponent,
      data: { title: 'Users', animation: 'users' }
    },
    {
      path: 'user-tree',
      component: UserTreePageComponent,
      data: { title: 'User Tree', animation: 'user-tree' }
    },
    {
      path: 'user-tree/:userId',
      component: UserTreePageComponent,
      data: { title: 'User Tree', animation: 'user-tree' }
    },
    {
      path: 'bookings',
      component: BookingsPageComponent,
      data: { title: 'Bookings', animation: 'bookings' }
    },
    // Appointments — the central calendar (Phase 1). Which appointments a member
    // sees on this board is decided by the backend (calendars × audiences).
    {
      path: 'appointments',
      component: AppointmentsPageComponent,
      canActivate: [emailVerifiedGuard],
      data: { title: 'Appointments', animation: 'appointments' }
    },
    {
      path: 'delete-account',
      component: DeleteAccountPageComponent,
      data: { title: 'Delete Account', animation: 'delete-account' }
    },
    {
      path: 'xshop-admin',
      component: XshopAdminPageComponent,
      data: { title: 'XShop Admin', animation: 'xshop-admin' }
    },
    {
      path: 'admin',
      canMatch: [adminGuard],
      canActivate: [adminGuard],
      loadChildren: () =>
        import('./admin/admin.routes')
          .then(routes => routes.ADMIN_ROUTES)
    }
  )
  .build();
